const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

function getCookie(name) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

async function apiFetch(path, options = {}, retry = true) {
  const method = options.method || 'GET';
  const headers = new Headers(options.headers || {});
  headers.set('Accept', 'application/json');
  if (options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
    const csrf = getCookie('csrf_token');
    if (csrf) headers.set('X-CSRF-Token', decodeURIComponent(csrf));
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && retry && !path.startsWith('/api/auth/')) {
    const refresh = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (refresh.ok) return apiFetch(path, options, false);
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Request failed: ${response.status}`);
  }
  return payload;
}

export const api = {
  login: (email, password, remember) =>
    apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, remember }),
    }),
  logout: () => apiFetch('/api/auth/logout', { method: 'POST' }),
  me: () => apiFetch('/api/auth/me'),
  stats: () => apiFetch('/api/stats'),
  sources: () => apiFetch('/api/sources'),
  createSource: (payload) =>
    apiFetch('/api/sources', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateSource: (id, payload) =>
    apiFetch(`/api/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deactivateSource: (id) => apiFetch(`/api/sources/${id}`, { method: 'DELETE' }),
  articles: (params) => apiFetch(`/api/articles?${new URLSearchParams(params)}`),
  article: (id) => apiFetch(`/api/articles/${id}`),
  extractArticle: (id, payload) =>
    apiFetch(`/api/articles/${id}/extract`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  setArticleStatus: (id, status) =>
    apiFetch(`/api/articles/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  bulkStatus: (articleIds, status) =>
    apiFetch('/api/articles/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ article_ids: articleIds, status }),
    }),
  collections: () => apiFetch('/api/collections'),
  collection: (id) => apiFetch(`/api/collections/${id}`),
  createCollection: (payload) =>
    apiFetch('/api/collections', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  markCollectionReady: (id) => apiFetch(`/api/collections/${id}/ready`, { method: 'POST' }),
  generateCollection: (id, publishToWordpress = false) =>
    apiFetch(`/api/collections/${id}/generate`, {
      method: 'POST',
      body: JSON.stringify({ publish_to_wordpress: publishToWordpress }),
    }),
  integrationSettings: () => apiFetch('/api/settings/integrations'),
  openaiModels: () => apiFetch('/api/settings/integrations/openai/models'),
  updateIntegrationSettings: (payload) =>
    apiFetch('/api/settings/integrations', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  generateCollectionItems: (id, publishToWordpress = false) =>
    apiFetch(`/api/collections/${id}/generate-items`, {
      method: 'POST',
      body: JSON.stringify({ publish_to_wordpress: publishToWordpress }),
    }),
};
